import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useWeb3Context } from '../../contexts/web3Context'
import { alert } from '../Alert/Alert'
import { getProfile } from '../../common/api'
import { getSharedPvPSocket } from '../../socket/pvpSharedSocket'
import {
  markRematchAttemptUsed,
  setAcceptedRematch,
  setAcceptedRematchCountdown,
  setDeclinedRematchMessage,
  setIncomingRematchRequest,
  setIsRespondingToRematch,
  setRematchRequestPendingBattleId,
} from '../../common/state/pvp/reducer'
import type { PvpAcceptedRematch, PvpIncomingRematchRequest } from '../../common/state/pvp/state'
import type { RootState } from '../../store'
import { PvPBattleRematchModal } from './PvPBattleRematchModal'

export const GlobalPvPRematchHost: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { address } = useWeb3Context()
  const user = useSelector((state: RootState) => state.userModule.user)
  const incomingRematchRequest = useSelector((state: RootState) => state.app.pvp.incomingRematchRequest)
  const isRespondingToRematch = useSelector((state: RootState) => state.app.pvp.isRespondingToRematch)
  const acceptedRematch = useSelector((state: RootState) => state.app.pvp.acceptedRematch)
  const acceptedRematchCountdown = useSelector((state: RootState) => state.app.pvp.acceptedRematchCountdown)
  const declinedRematchMessage = useSelector((state: RootState) => state.app.pvp.declinedRematchMessage)

  const rematchAcceptInsufficient = React.useMemo(() => {
    if (!incomingRematchRequest) return false
    const stake = Number(incomingRematchRequest.rematchStakeAmount ?? 0)
    if (!Number.isFinite(stake) || stake <= 0) return false
    const rt = String(incomingRematchRequest.rematchRoomType || '').toLowerCase()
    if (rt === 'ecs') {
      const ecs = Number(user?.ecsTokenAmount ?? 0)
      return ecs + 1e-9 < stake
    }
    if (rt === 'usdt') {
      const usdt = Number(user?.usdt ?? 0)
      return usdt + 1e-9 < stake
    }
    return false
  }, [
    incomingRematchRequest,
    incomingRematchRequest?.rematchStakeAmount,
    incomingRematchRequest?.rematchRoomType,
    user?.ecsTokenAmount,
    user?.usdt,
  ])

  const rematchAcceptDisabledHint = React.useMemo(() => {
    if (!rematchAcceptInsufficient || !incomingRematchRequest) return null
    const stake = Number(incomingRematchRequest.rematchStakeAmount ?? 0)
    const rt = String(incomingRematchRequest.rematchRoomType || '').toLowerCase()
    if (rt === 'ecs') {
      return t('pvp.battle.rematchAcceptInsufficientEcs', {
        required: stake.toFixed(2),
        current: Number(user?.ecsTokenAmount ?? 0).toFixed(2),
      })
    }
    if (rt === 'usdt') {
      return t('pvp.battle.rematchAcceptInsufficientUsdt', {
        required: stake.toFixed(2),
        current: Number(user?.usdt ?? 0).toFixed(2),
      })
    }
    return t('pvp.battle.rematchAcceptInsufficientGeneric')
  }, [rematchAcceptInsufficient, incomingRematchRequest, user?.ecsTokenAmount, user?.usdt, t])

  React.useEffect(() => {
    if (address) return
    dispatch(setIncomingRematchRequest(null))
    dispatch(setIsRespondingToRematch(false))
    dispatch(setAcceptedRematch(null))
    dispatch(setAcceptedRematchCountdown(null))
    dispatch(setDeclinedRematchMessage(null))
    dispatch(setRematchRequestPendingBattleId(null))
  }, [address, dispatch])

  React.useEffect(() => {
    if (!address) return

    const socket = getSharedPvPSocket(address)
    const onConnect = () => {
      socket.emit('join-wallet-room', { walletAddress: address })
      socket.emit('user:online', { walletAddress: address })
    }
    const onRematchRequested = async (data: PvpIncomingRematchRequest) => {
      dispatch(setDeclinedRematchMessage(null))
      dispatch(setAcceptedRematch(null))
      dispatch(setAcceptedRematchCountdown(null))
      dispatch(setRematchRequestPendingBattleId(null))
      dispatch(markRematchAttemptUsed(data.previousBattleId))
      if (address) {
        try {
          await getProfile(address, '')
        } catch {
          /* keep stale balance; modal still uses payload stake */
        }
      }
      dispatch(setIncomingRematchRequest(data))
    }
    const onRematchAccepted = (data: PvpAcceptedRematch) => {
      dispatch(setIncomingRematchRequest(null))
      dispatch(setIsRespondingToRematch(false))
      dispatch(setRematchRequestPendingBattleId(null))
      dispatch(setDeclinedRematchMessage(null))
      dispatch(markRematchAttemptUsed(data.previousBattleId))
      dispatch(setAcceptedRematch(data))
      dispatch(setAcceptedRematchCountdown(data.countdownSeconds))
    }
    const onRematchDeclined = (data: {
      previousBattleId: string
      responderWallet: string
      responderNickname: string
      reason: string
    }) => {
      dispatch(setIncomingRematchRequest(null))
      dispatch(setIsRespondingToRematch(false))
      dispatch(setAcceptedRematch(null))
      dispatch(setAcceptedRematchCountdown(null))
      dispatch(setRematchRequestPendingBattleId(null))
      dispatch(markRematchAttemptUsed(data.previousBattleId))

      if (address && data.responderWallet === address) {
        dispatch(setDeclinedRematchMessage(null))
        return
      }

      dispatch(
        setDeclinedRematchMessage(
          data.reason && data.reason !== 'declined'
            ? t('pvp.battle.rematchDeclinedCouldNotAccept', { name: data.responderNickname })
            : t('pvp.battle.rematchDeclinedByOpponent', { name: data.responderNickname }),
        ),
      )
    }

    socket.on('connect', onConnect)
    socket.on('pvp:rematch-requested', onRematchRequested)
    socket.on('pvp:rematch-accepted', onRematchAccepted)
    socket.on('pvp:rematch-declined', onRematchDeclined)

    if (socket.connected) {
      onConnect()
    }

    return () => {
      socket.off('connect', onConnect)
      socket.off('pvp:rematch-requested', onRematchRequested)
      socket.off('pvp:rematch-accepted', onRematchAccepted)
      socket.off('pvp:rematch-declined', onRematchDeclined)
    }
  }, [address, dispatch])

  const handleRespondToRematch = React.useCallback(
    (accept: boolean) => {
      if (!address || !incomingRematchRequest || isRespondingToRematch) return

      const socket = getSharedPvPSocket(address)
      dispatch(setIsRespondingToRematch(true))
      socket.emit(
        'pvp:rematch-response',
        {
          walletAddress: address,
          battleId: incomingRematchRequest.previousBattleId,
          accept,
        },
        (response: { success?: boolean; error?: string }) => {
          if (!response?.success && response?.error) {
            dispatch(setIsRespondingToRematch(false))
            alert.open(response.error)
          }
        },
      )
    },
    [address, dispatch, incomingRematchRequest, isRespondingToRematch],
  )

  React.useEffect(() => {
    if (!acceptedRematch || acceptedRematchCountdown == null) return

    if (acceptedRematchCountdown <= 0) {
      dispatch(setAcceptedRematch(null))
      dispatch(setAcceptedRematchCountdown(null))
      navigate(`/pvp/battle/${acceptedRematch.battleId}?roomId=${acceptedRematch.roomId}`)
      return
    }

    const timer = window.setTimeout(() => {
      dispatch(setAcceptedRematchCountdown(acceptedRematchCountdown - 1))
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [acceptedRematch, acceptedRematchCountdown, dispatch, navigate])

  return (
    <>
      <PvPBattleRematchModal
        open={!!incomingRematchRequest}
        title={t('pvp.battle.rematchRequestTitle')}
        description={t('pvp.battle.rematchRequestDescription', {
          name: incomingRematchRequest?.requesterNickname || t('pvp.opponent'),
        })}
        confirmLabel={t('pvp.directChallengeAccept')}
        cancelLabel={t('pvp.directChallengeDecline')}
        onConfirm={() => handleRespondToRematch(true)}
        onCancel={() => handleRespondToRematch(false)}
        isLoading={isRespondingToRematch}
        accent="amber"
        disableClose
        confirmDisabled={rematchAcceptInsufficient}
        confirmDisabledHint={rematchAcceptDisabledHint}
      />

      <PvPBattleRematchModal
        open={!!declinedRematchMessage}
        title={t('pvp.battle.rematchDeclinedTitle')}
        description={declinedRematchMessage || ''}
        confirmLabel={t('pvp.close')}
        onConfirm={() => dispatch(setDeclinedRematchMessage(null))}
        accent="red"
      />

      <PvPBattleRematchModal
        open={!!acceptedRematch}
        title={t('pvp.battle.rematchAcceptedTitle')}
        description={
          acceptedRematch?.stakeLabel && acceptedRematch.stakeLabel !== 'free'
            ? t('pvp.battle.rematchAcceptedDescriptionWithStake', { stake: acceptedRematch.stakeLabel })
            : t('pvp.battle.rematchAcceptedDescription')
        }
        countdownSeconds={acceptedRematchCountdown}
        accent="blue"
        disableClose
      />
    </>
  )
}

export default GlobalPvPRematchHost
